import { useState } from 'react';
import { ArrowLeft, Copy, Pencil, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { Alert } from '@/components/ui/Alert';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Section, DetailKV } from '@/components/ui/DetailSection';
import { AppearancePreview } from './AppearancePreview';
import {
  conflictExtra,
  isConflict,
  toastUnlessConflict,
  useAppearanceDetail,
  usePatchAppearance,
  useToggleAppearance,
  useTogglePaletteItem,
} from '../api';
import { hexToRgb } from '../utils/appearance';
import type { AppearanceDetail, Library, PaletteItem } from '../types';

type InactiveKey = 'model_pattern' | 'model_finish';

export function AppearanceDetailPanel({
  id,
  canEdit,
  onEdit,
  onDuplicate,
  onNavigate,
  onBack,
}: {
  id: string;
  canEdit: boolean;
  onEdit: (detail: AppearanceDetail) => void;
  onDuplicate: (detail: AppearanceDetail) => void;
  onNavigate: (library: Library, id: string) => void;
  onBack: () => void;
}) {
  const { data: detail, isLoading, isError } = useAppearanceDetail(id);
  const toggle = useToggleAppearance();
  const patch = usePatchAppearance();
  const restorePattern = useTogglePaletteItem('pattern');
  const restoreFinish = useTogglePaletteItem('finish');
  const [inactive, setInactive] = useState<InactiveKey[]>([]);

  if (isLoading) return <SpinnerOverlay />;
  if (isError || !detail)
    return <EmptyState title="Vzhled se nepodařilo načíst" description="Zkuste ho vybrat znovu." />;

  const setActive = async (active: boolean) => {
    try {
      await toggle.mutateAsync({ id: detail.id, active });
      setInactive([]);
      toast.success(active ? 'Vzhled byl aktivován' : 'Vzhled byl deaktivován');
    } catch (error) {
      if (isConflict(error)) setInactive(conflictExtra(error).inactive ?? []);
      toastUnlessConflict(error);
    }
  };

  const resolve = async (key: InactiveKey, action: 'restore' | 'remove') => {
    const item = detail[key];
    try {
      if (action === 'remove') await patch.mutateAsync({ id: detail.id, input: { [key]: null } });
      else if (item)
        await (key === 'model_pattern' ? restorePattern : restoreFinish).mutateAsync({
          id: item.id,
          active: true,
        });
      const rest = inactive.filter((entry) => entry !== key);
      setInactive(rest);
      if (!rest.length) await setActive(true);
    } catch (error) {
      toastUnlessConflict(error);
      if (isConflict(error)) toast.error('Položku palety nelze obnovit');
    }
  };

  const components: [Library, string, PaletteItem | null][] = [
    ['patterns', 'Dezén', detail.model_pattern],
    ['finishes', 'Úprava povrchu', detail.model_finish],
  ];
  const color = detail.model_color;

  return (
    <article className="h-full overflow-y-auto" aria-label="Detail vzhledu">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-150 px-5 py-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label="Zpět na seznam"
            onClick={onBack}
          >
            <ArrowLeft />
          </Button>
          <h2 className="text-sm font-semibold">Detail vzhledu</h2>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={toggle.isLoading}
              onClick={() => setActive(!detail.is_active)}
            >
              <Power />
              {detail.is_active ? 'Deaktivovat' : 'Aktivovat'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDuplicate(detail)}>
              <Copy />
              Duplikovat
            </Button>
            <Button variant="outline" size="sm" onClick={() => onEdit(detail)}>
              <Pencil />
              Upravit
            </Button>
          </div>
        )}
      </div>
      <div className="px-5 py-5">
        {inactive.length > 0 && (
          <Alert variant="warning" title="Vzhled nelze aktivovat" className="mb-4">
            <p>Vzhled odkazuje na položku, která byla odebrána z palety modelu.</p>
            <ul className="mt-2 space-y-2">
              {inactive.map((key) => (
                <li key={key} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {key === 'model_pattern' ? 'Dezén' : 'Úprava povrchu'} {detail[key]?.code}
                  </span>
                  <Button size="xs" variant="outline" onClick={() => resolve(key, 'restore')}>
                    Vrátit do palety
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => resolve(key, 'remove')}>
                    Odebrat ze vzhledu
                  </Button>
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <AppearancePreview
          hex={detail.hex ?? color?.hex ?? null}
          patternImageUrl={detail.model_pattern?.classified_solution.image_url}
          finishImageUrl={detail.model_finish?.classified_solution.image_url}
          label={detail.title}
          className="h-44 w-full"
        />
        <div className="mt-5 mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-neutral-900 break-words">{detail.title}</h2>
            {!detail.is_active && <Badge variant="warning">Neaktivní</Badge>}
          </div>
          {detail.custom_title && (
            <p className="font-mono text-xs text-neutral-500 mt-1">{detail.sys_title}</p>
          )}
        </div>
        {color && (
          <Section title="Barva">
            <DetailKV label="Název" value={color.title} />
            <DetailKV
              label="HEX"
              value={
                <span className="inline-flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-sm border border-black/10"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span className="font-mono">{color.hex?.toUpperCase()}</span>
                </span>
              }
            />
            <DetailKV label="RGB" value={hexToRgb(color.hex)} />
            <DetailKV label="Kód barvy" value={color.color_code || '—'} />
            <DetailKV label="Kategorie" value={color.category_label} />
            <DetailKV
              label="Vlastnosti"
              value={
                color.properties.length ? (
                  <span className="flex flex-wrap gap-1">
                    {color.properties.map((item) => (
                      <Badge key={item.id} variant="outline">
                        {item.title}
                      </Badge>
                    ))}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <DetailKV label="Číslo" value={color.code} />
            {!color.is_active && (
              <p className="text-xs text-amber-700 mt-2">Odebrána z palety modelu</p>
            )}
            <Button
              variant="link"
              size="sm"
              className="mt-1 px-0"
              onClick={() => onNavigate('colors', color.id)}
            >
              Detail barvy
            </Button>
          </Section>
        )}
        <Section title="Dezén a úprava povrchu">
          {components.map(([library, label, item]) => (
            <div
              key={library}
              className="flex items-center justify-between gap-3 py-3 border-b border-neutral-100 last:border-0"
            >
              <div className="flex items-center gap-3 min-w-0">
                <AppearancePreview
                  patternImageUrl={item?.classified_solution.image_url}
                  label={item?.title || 'Žádná složka'}
                  className="w-12 h-12 shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase text-neutral-500">{label}</p>
                  <p className="text-sm font-medium mt-1 break-words">
                    {item ? (
                      <>
                        <span className="font-mono text-neutral-500">{item.code}</span> {item.title}
                      </>
                    ) : (
                      'Žádná složka'
                    )}
                  </p>
                  {item && !item.is_active && (
                    <p className="text-xs text-amber-700">Odebráno z palety modelu</p>
                  )}
                </div>
              </div>
              {item ? (
                <Button variant="link" size="sm" onClick={() => onNavigate(library, item.id)}>
                  Detail
                </Button>
              ) : (
                canEdit && (
                  <Button variant="link" size="sm" onClick={() => onEdit(detail)}>
                    Vybrat
                  </Button>
                )
              )}
            </div>
          ))}
        </Section>
      </div>
    </article>
  );
}
